import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { http } from '@/utils'
import type { IAccount, SymbolListRow } from '@/types'
import { CACHE_TTL, CHUNK_SIZE } from '@/constants'
import { useAccountStore } from '@/store'
import { useMarketCalendarStore } from '@/composables/marketCalendar'
import type { MergeForexSymbol, SymbolSpecification } from './types'

dayjs.extend(utc)
dayjs.extend(timezone)

// 定义合并后的符号数据类型

function chunk<T>(arr: T[], size: number): T[][] {
  const res: T[][] = []
  for (let i = 0; i < arr.length; i += size) res.push(arr.slice(i, i + size))
  return res
}

export const useSymbolInfoStore = defineStore('symbolInfo', () => {
  // 按账号隔离的缓存：Map<accountKey, Map<symbol, data>>
  const accountCaches = ref(new Map<string, Map<string, MergeForexSymbol>>())
  // 正在进行的请求，防止重复请求
  const pendingRequests = ref(new Map<string, Promise<void>>())

  const accountStore = useAccountStore()
  const serverOffset = computed(
    () => (accountStore.selectedAccount?.server_offset_seconds || 10080) / 3600,
  )
  const marketStore = useMarketCalendarStore()

  // 获取当前账号的缓存
  const symbolSpecificationMap = computed(() => {
    const accountKey = getAccountKey(accountStore.selectedAccount)
    if (!accountCaches.value.has(accountKey)) {
      accountCaches.value.set(accountKey, new Map())
    }
    return accountCaches.value.get(accountKey)!
  })

  // 生成账号唯一标识
  function getAccountKey(account: IAccount | null): string {
    if (!account) return 'default'
    return `${account.server_name}_${account.loginid}`
  }

  // 清空当前账号缓存
  function clearCurrentCache() {
    const accountKey = getAccountKey(accountStore.selectedAccount)
    accountCaches.value.delete(accountKey)
    // 清空相关的待处理请求
    for (const [key, _] of pendingRequests.value) {
      if (key.startsWith(accountKey)) {
        pendingRequests.value.delete(key)
      }
    }
    console.log('[symbolSpec] 当前账号缓存已清空:', accountKey)
  }

  // 清空所有缓存
  function clearAllCache() {
    accountCaches.value.clear()
    pendingRequests.value.clear()
    console.log('[symbolSpec] 所有账号缓存已清空')
  }

  // 获取缓存状态（用于调试）
  function getCacheStatus() {
    const accountKey = getAccountKey(accountStore.selectedAccount)
    const currentCache = accountCaches.value.get(accountKey)

    return {
      currentAccount: accountKey,
      cachedCount: currentCache?.size || 0,
      cachedSymbols: currentCache ? Array.from(currentCache.keys()) : [],
      allAccounts: Array.from(accountCaches.value.keys()),
      totalCaches: accountCaches.value.size,
      pendingRequests: pendingRequests.value.size,
    }
  }

  async function fetchSpecs(symbols: SymbolListRow[], account: IAccount) {
    if (!symbols.length) return
    const now = Date.now()
    const accountKey = getAccountKey(account)

    const need = symbols.filter((sym) => {
      const cached = symbolSpecificationMap.value.get(sym.symbol)
      return !cached || now - cached.fetchedAt > CACHE_TTL
    })
    if (!need.length) return

    // 去重和防重复请求
    const requestKey = `${accountKey}-${need
      .map((s) => s.symbol)
      .sort()
      .join(',')}`
    if (pendingRequests.value.has(requestKey)) {
      // 等待已有的请求完成
      await pendingRequests.value.get(requestKey)
      return
    }

    // 创建新的请求 Promise
    const requestPromise = (async () => {
      try {
        for (const part of chunk(need, CHUNK_SIZE)) {
          await fetchOneBatch(part, account, now)
        }
      } finally {
        pendingRequests.value.delete(requestKey)
      }
    })()

    pendingRequests.value.set(requestKey, requestPromise)
    await requestPromise
  }

  async function fetchOneBatch(part: SymbolListRow[], account: IAccount, now: number) {
    try {
      const data = await http.post<Record<string, SymbolSpecification>>('/api/trade/symbol_info', {
        symbol: part.map((s) => s.symbol).join(','), // 逗号串
        exchange_name: account.server_name,
        loginid: account.loginid,
      })
      Object.values(data).forEach((symbolInfo) => {
        // 注册到 market calendar store
        if (symbolInfo.sessions_trade) {
          marketStore.registerSessions(
            symbolInfo.symbol,
            symbolInfo.sessions_trade,
            serverOffset.value,
          )
        }

        // 保存到 symbol specification 缓存
        symbolSpecificationMap.value.set(symbolInfo.symbol, {
          ...symbolInfo,
          fetchedAt: now,
        })
      })
    } catch (err) {
      console.error('[symbolSpec] fetchSpecs error:', err)
    }
  }

  return {
    symbolSpecificationMap,
    fetchSpecs,
    clearCurrentCache,
    clearAllCache,
    getCacheStatus,
  }
})

export default useSymbolInfoStore
